import { resources } from "@constants/hardcoded-strings";
import { VERIFICATION_SERVICES_REFERENCE_DATA_TYPES, VerificationServicesReferenceData } from "@constants/reference-data";
import { VERIFICATION_SERVICES_DETAILS_URL, VERIFICATION_SERVICES_URL } from "@constants/routes";
import { ButtonWithIcon } from "@containers/common/button-with-icon/button-with-icon";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { useModalContext } from "@context/use-modal-context";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { getVerificationServiceConfigurationsReduxRequest } from "@redux-requests/verification-services";
import {
    setTableSettingsTableFilters,
    setTableSettingsTablePagination
} from "@redux/actions/table-settings-actions";
import { createVerificationServiceRequest } from "@requests/service-configuration";
import { navigateToUtil } from "@utils/navigation-util";
import queryString from "query-string";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AddVerificationServiceModal } from "./add-verification-service-modal/add-verification-service-modal";
import styles from "./verification-services-container.less";
import { VerificationServicesTableContainer } from "./verification-services-table-container/verification-services-table-container";
import { formatMessageTemplates } from "@formatters/message-templates/message-template-formatter";
import { getActiveElements, getActiveReferenceData } from "@utils/data-util";
import { isAxnAdminUser } from "@utils/permissions-util";
import { getSessionState, getTableSettingsState } from "@redux/selectors/root-selectors";
import { getVerificationServicesDataState } from "@redux/selectors/verification-services-selectors";

export const VerificationServicesContainer = () => {
    const modalContext = useModalContext();

    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const {
        isLoadingVerificationServiceConfigurations,
        verificationServiceConfigurations
    } = useSelector(getVerificationServicesDataState);
    const { verificationServices: verificationServicesTableState } = useSelector(getTableSettingsState);
    const referenceData = useReferenceDataSelector<VerificationServicesReferenceData>(VERIFICATION_SERVICES_REFERENCE_DATA_TYPES);

    const [showArchivedServices, setShowArchivedServices] = useState(false);
    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadVerificationServiceConfigurations, setShouldLoadVerificationServiceConfigurations] = useState(true);
    const routerLocation = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!referenceData.isLoading.verificationServices && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                VERIFICATION_SERVICES_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.AXNADMIN_CORE,
                CommonReferenceDataPageTypes.VERIFICATION_SERVICES
            );
        }
    }, [referenceData.isLoading.verificationServices, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.axnCountries &&
            referenceData.axnIdentityProviders &&
            referenceData.axnRelyingParties &&
            referenceData.selfServiceVerificationMessageTemplates &&
            referenceData.selfServiceVerificationAttributes &&
            referenceData.axnAttributeTypeFields &&
            referenceData.rpServiceTemplates &&
            referenceData.rpServiceTypes &&
            referenceData.simpleAXNWorkflows &&
            referenceData.axnAdminWorkflows &&
            referenceData.axnAvailablePropertyCategories &&
            referenceData.attributeClassifications &&
            referenceData.axnAttributeLabels &&
            referenceData.axnAttributeTypes &&
            referenceData.rpServiceAttributeProperties &&
            referenceData.rpServiceAttributePropertyMaps &&
            referenceData.axnAttributeTypeFieldCustomizations
        );
    }, [
        referenceData.axnCompanies,
        referenceData.axnCountries,
        referenceData.axnIdentityProviders,
        referenceData.axnRelyingParties,
        referenceData.selfServiceVerificationMessageTemplates,
        referenceData.selfServiceVerificationAttributes,
        referenceData.axnAttributeTypeFields,
        referenceData.rpServiceTemplates,
        referenceData.rpServiceTypes,
        referenceData.simpleAXNWorkflows,
        referenceData.axnAdminWorkflows,
        referenceData.axnAvailablePropertyCategories,
        referenceData.attributeClassifications,
        referenceData.axnAttributeLabels,
        referenceData.axnAttributeTypes,
        referenceData.rpServiceAttributeProperties,
        referenceData.rpServiceAttributePropertyMaps,
        referenceData.axnAttributeTypeFieldCustomizations
    ]);

    useEffect(() => {
        if (
            isReferenceDataPresent &&
            !isLoadingVerificationServiceConfigurations &&
            shouldLoadVerificationServiceConfigurations
        ) {
            setShouldLoadVerificationServiceConfigurations(false);
            getVerificationServiceConfigurationsReduxRequest(dispatch)(
                referenceData.axnCompanies,
                referenceData.axnRelyingParties,
                referenceData.rpServiceTypes,
                referenceData.simpleAXNWorkflows,
                referenceData.axnAdminWorkflows
            );
        }
    }, [isReferenceDataPresent, isLoadingVerificationServiceConfigurations, shouldLoadVerificationServiceConfigurations]);

    useEffect(() => {
        if (routerLocation.search) {
            const queryParameters = queryString.parse(routerLocation.search, { decode: true });


            const tableFilters = [{
                id: "workflow-name-column",
                value: queryParameters.workflowName as string
            }];

            if (queryParameters.relyingParty) {
                tableFilters.push({
                    id: "relying-party-column",
                    value: queryParameters.relyingParty as string
                });
            }

            dispatch(setTableSettingsTableFilters(TableSettingsTableTypes.VERIFICATION_SERVICES, tableFilters));
            navigateToUtil(VERIFICATION_SERVICES_URL, navigate, routerLocation.pathname);
        }

    }, [routerLocation?.search]);

    const activeVerificationServiceConfigurations = useMemo(() => {
        return getActiveElements(verificationServiceConfigurations);
    }, [verificationServiceConfigurations]);

    const isAdminUser = useMemo(() => isAxnAdminUser(session.user), [session.user]);

    const filteredRpServiceTemplates = useMemo(() => {
        if (isReferenceDataPresent) {
            const { rpServiceTemplates } = referenceData;

            const { rpServiceTemplates: activeServiceTemplates } = getActiveReferenceData({
                rpServiceTemplates
            });

            return isAdminUser ?
                activeServiceTemplates :
                (activeServiceTemplates.filter(activeRpServiceTemplate => {
                    if (activeRpServiceTemplate.organizationID) {
                        return activeRpServiceTemplate.organizationID === session.user.organizationID;
                    } else {
                        return true;
                    }
                }
                ));
        }

        return [];
    }, [isReferenceDataPresent, referenceData.rpServiceTemplates, session.user]);


    const renderAddVerificationServiceModal = useMemo(() => (onModalClose: () => void) => {
        return (
            <AddVerificationServiceModal
                onModalClose={onModalClose}
                redirectUrl={VERIFICATION_SERVICES_DETAILS_URL}
                userPermissions={session.userPermissions}
                verificationServices={activeVerificationServiceConfigurations}
                rpServiceTemplates={filteredRpServiceTemplates}
                updateStateForNewService={null}
                createServiceRequest={createVerificationServiceRequest}
            />
        );
    }, [activeVerificationServiceConfigurations, filteredRpServiceTemplates, session.userPermissions]);

    const isLoading = useMemo(() => {
        return isLoadingVerificationServiceConfigurations || referenceData.isLoading.verificationServices;
    }, [referenceData.isLoading.verificationServices, isLoadingVerificationServiceConfigurations]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            verificationServiceConfigurations &&
            isReferenceDataPresent &&
            !shouldLoadVerificationServiceConfigurations
        );
    }, [isLoading, verificationServiceConfigurations, isReferenceDataPresent, shouldLoadVerificationServiceConfigurations]);

    const handleSetShowArchivedServices = () => {
        dispatch(setTableSettingsTablePagination(TableSettingsTableTypes.VERIFICATION_SERVICES, { pageIndex: 0 }));
        setShowArchivedServices(!showArchivedServices);
    };

    const headerButton = useMemo(() => {
        if (session.userPermissions?.canCreateRPServices) {
            const onClick = () => {
                modalContext.setModalState({
                    modalType: ModalTypes.AddVerificationService,
                    renderFunction: renderAddVerificationServiceModal
                });
            };
            return (
                <ButtonWithIcon
                    id="verification-services-add-service"
                    title={resources.pages.verificationServices.headerButton}
                    text={resources.pages.verificationServices.headerButton}
                    onClick={onClick}
                    icon={faPlusCircle}
                    disabled={isLoading || !isLoaded}
                />
            );
        } else {
            return null;
        }
    }, [isLoading, isLoaded, session.userPermissions, renderAddVerificationServiceModal]);

    const filteredVerificationServiceConfigurations = useMemo(() => {
        if (verificationServiceConfigurations && showArchivedServices) {
            return verificationServiceConfigurations.filter(config => config.status === EntityStatusTypes.INACTIVE);
        } else if (verificationServiceConfigurations && !showArchivedServices) {
            return verificationServiceConfigurations.filter(config => config.status === EntityStatusTypes.ACTIVE);
        } else {
            return [];
        }
    }, [verificationServiceConfigurations, showArchivedServices]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader
                title={resources.pages.verificationServices.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"verification-service-list-page-header"}
                    />
                }
                headerButton={headerButton}
            />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <>
                        <div className={styles.toggleServicesSection}>
                            <ToggleFormGroup
                                checked={showArchivedServices}
                                controlId={"show-archived-verification-services-toggle"}
                                disabled={false}
                                isRequired={false}
                                labelWidth={LabelWidthTypes.EXTRA_SMALL}
                                label={resources.pages.verificationServices.filters.showArchivedServices.label}
                                ariaLabel={resources.pages.verificationServices.filters.showArchivedServices.label}
                                onChange={handleSetShowArchivedServices}
                                value={`${showArchivedServices}`}
                                checkedIcon={false}
                                uncheckedIcon={false}
                            />
                        </div>
                        <VerificationServicesTableContainer
                            verificationServiceConfigurations={filteredVerificationServiceConfigurations}
                            isPageHeight={true}
                            axnCountries={referenceData.axnCountries}
                            axnCompanies={referenceData.axnCompanies}
                            relyingParties={referenceData.axnRelyingParties}
                            rpServiceTypes={referenceData.rpServiceTypes}
                            simpleAXNWorkflows={referenceData.simpleAXNWorkflows}
                            simpleAXNAdminWorkflows={referenceData.axnAdminWorkflows}
                            axnAvailablePropertyCategories={referenceData.axnAvailablePropertyCategories}
                            axnIdentityProviders={referenceData.axnIdentityProviders}
                            messageTemplates={formatMessageTemplates(referenceData.selfServiceVerificationMessageTemplates, referenceData.axnCompanies)}
                            showArchivedServices={showArchivedServices}
                            tableState={verificationServicesTableState}
                            axnAttributeTypeFields={referenceData.axnAttributeTypeFields}
                            axnAttributeTypeFieldCustomizations={referenceData.axnAttributeTypeFieldCustomizations}
                            selfServiceVerificationAttributes={referenceData.selfServiceVerificationAttributes}
                            attributeClassifications={referenceData.attributeClassifications}
                            attributeLabels={referenceData.axnAttributeLabels}
                            attributeTypes={referenceData.axnAttributeTypes}
                            rpServiceAttributeProperties={referenceData.rpServiceAttributeProperties}
                            rpServiceAttributePropertyMaps={referenceData.rpServiceAttributePropertyMaps}
                        />
                    </>
                )}
            </ContentPageBody>
        </CardContainer>
    );
};